import { useCallback, useMemo } from 'preact/hooks';
import { TCFStack } from '@usercentrics/cmp-browser-sdk';
import { TCFState } from '../../contexts/TcfContext';
import { sortById } from '../../modules/sort';

export type StackProps = {
  checked: boolean;
  onStackConsentToggle: () => void;
  stack: TCFStack;
};

const MapStacks: (props: TCFState) => StackProps[] = ({ setTcfData, tcfData }) => {
  const onStackToggleHandler = useCallback(
    (newConsentChecked: boolean, id: number): void => {
      if (!tcfData) {
        return;
      }
      const toggledStack = tcfData.stacks.find((stack) => stack.id === id);

      if (toggledStack) {
        setTcfData({
          ...tcfData,
          purposes: tcfData.purposes.map((purpose) =>
            toggledStack.purposeIds.includes(purpose.id)
              ? {
                  ...purpose,
                  consent: newConsentChecked,
                }
              : purpose,
          ),
          specialFeatures: tcfData.specialFeatures.map((specialFeature) =>
            toggledStack.specialFeatureIds.includes(specialFeature.id)
              ? {
                  ...specialFeature,
                  consent: newConsentChecked,
                }
              : specialFeature,
          ),
        });
      }
    },
    [setTcfData, tcfData],
  );

  const sortedStacks = useMemo(() => {
    if (tcfData?.stacks) {
      return sortById(tcfData.stacks);
    }

    return [];
  }, [tcfData]);

  if (!tcfData) {
    return [];
  }

  return sortedStacks.map((stack) => {
    const purposes = tcfData.purposes.filter((purpose) => stack.purposeIds.includes(purpose.id));
    const specialFeatures = tcfData.specialFeatures.filter((specialFeature) =>
      stack.specialFeatureIds.includes(specialFeature.id),
    );
    // A stack is checked if SOME containing purposes or specialFeatures are checked
    const checked =
      purposes.some((purpose) => purpose.consent) || specialFeatures.some((specialFeature) => specialFeature.consent);

    const onStackConsentToggle = (): void => {
      onStackToggleHandler(!checked, stack.id);
    };

    return {
      checked,
      onStackConsentToggle,
      stack,
    };
  });
};

export default MapStacks;
